import { supabase } from "@/integrations/supabase/client";
import { normalizeDocumentIntelligence, type NormalizedDocumentIntelligence } from "@/lib/document-intelligence";

interface UploadDealDocumentParams {
  dealId: string;
  docType: string;
  file: File;
  userId: string;
}

interface ParseDealDocumentParams {
  documentId: string;
  docType: string;
  scanTemplateId: string;
}

interface UploadAndParseDealDocumentParams extends UploadDealDocumentParams {
  scanTemplateId?: string | null;
}

interface ParseDealDocumentResult {
  extracted_data?: Record<string, unknown> | null;
  intelligence?: NormalizedDocumentIntelligence | null;
}

export interface UploadAndParseDealDocumentResult extends ParseDealDocumentResult {
  documentId: string;
  fileUrl: string;
}

export async function uploadDealDocument({ dealId, docType, file, userId }: UploadDealDocumentParams) {
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const storagePath = `${userId}/${dealId}/${Date.now()}_${safeName}`;

  const { error: uploadErr } = await supabase.storage
    .from("deal-documents")
    .upload(storagePath, file, { upsert: true });

  if (uploadErr) throw new Error(uploadErr.message);

  const { data: docRow, error: insertErr } = await supabase
    .from("deal_documents")
    .insert({
      deal_id: dealId,
      user_id: userId,
      doc_type: docType,
      file_url: storagePath,
    })
    .select("id")
    .single();

  if (insertErr || !docRow?.id) throw new Error(insertErr?.message || "Failed to create document record.");

  return { documentId: docRow.id, fileUrl: storagePath };
}

// When the parse-document edge worker is killed during the response flush
// (wall-clock / memory kill after DB writes completed), supabase.functions.invoke
// surfaces a generic FunctionsHttpError even though the row in deal_documents is
// already fully populated. In that case we re-fetch the row and, if the parse
// results are present, treat the invocation as successful.
async function recoverParsedRowFromDb(documentId: string): Promise<ParseDealDocumentResult | null> {
  const { data: row } = await supabase
    .from("deal_documents")
    .select("id, doc_type, document_lane, document_subtype, risk_flags, recommended_underwriting_adjustments, confidence, executive_interpretation, brickshare_field_mapping, evidence_log, extracted_data")
    .eq("id", documentId)
    .maybeSingle();

  if (!row) return null;
  const hasParsedPayload = Boolean(row.extracted_data) || Boolean(row.executive_interpretation);
  if (!hasParsedPayload) return null;

  const intelligence = normalizeDocumentIntelligence(row);
  return {
    extracted_data: (row.extracted_data as Record<string, unknown> | null) ?? null,
    intelligence,
  };
}

export async function parseDealDocument({ documentId, docType, scanTemplateId }: ParseDealDocumentParams): Promise<ParseDealDocumentResult> {
  const { data: parseData, error: parseErr } = await supabase.functions.invoke("parse-document", {
    body: {
      document_id: documentId,
      scan_template_id: scanTemplateId,
    },
  });

  if (parseErr) {
    // Last-mile failure recovery: the worker may have completed DB writes and
    // been killed during response flush. Check the DB before surfacing an error.
    const recovered = await recoverParsedRowFromDb(documentId);
    if (recovered) {
      console.warn("parse-document invoke failed but DB row is populated; recovered from deal_documents.", parseErr);
      return recovered;
    }
    throw new Error(parseErr.message || "Document parsing failed.");
  }

  if (!parseData?.intelligence && !parseData?.extracted_data) {
    // Also recover the empty-response case — same worker-kill signature but with
    // supabase-js returning `{ data: null, error: null }`.
    const recovered = await recoverParsedRowFromDb(documentId);
    if (recovered) return recovered;
    return { extracted_data: null, intelligence: null };
  }

  return {
    extracted_data: parseData?.extracted_data ?? null,
    intelligence: normalizeDocumentIntelligence({
      id: documentId,
      doc_type: docType,
      document_lane: parseData?.intelligence?.document_lane,
      document_subtype: parseData?.intelligence?.document_subtype,
      risk_flags: parseData?.intelligence?.risk_flags,
      recommended_underwriting_adjustments: parseData?.intelligence?.recommended_underwriting_adjustments,
      confidence: parseData?.intelligence?.confidence,
      executive_interpretation: parseData?.intelligence?.executive_interpretation,
      brickshare_field_mapping: parseData?.intelligence?.brickshare_field_mapping,
      evidence_log: parseData?.intelligence?.evidence_log,
      extracted_data: parseData?.intelligence,
    }),
  };
}


export async function uploadAndParseDealDocument({
  dealId,
  docType,
  file,
  userId,
  scanTemplateId,
}: UploadAndParseDealDocumentParams): Promise<UploadAndParseDealDocumentResult> {
  const upload = await uploadDealDocument({ dealId, docType, file, userId });

  if (!scanTemplateId) {
    return {
      documentId: upload.documentId,
      fileUrl: upload.fileUrl,
      extracted_data: null,
      intelligence: null,
    };
  }

  const parsed = await parseDealDocument({
    documentId: upload.documentId,
    docType,
    scanTemplateId,
  });

  return {
    documentId: upload.documentId,
    fileUrl: upload.fileUrl,
    extracted_data: parsed.extracted_data ?? null,
    intelligence: parsed.intelligence ?? null,
  };
}